// Pure logic for the Revenue Tracker: prices the FCST vs Actuals hours from /dashboard/reports/revenue
// with each project's BillingRate in effect that month, and compares the outlook to a locked baseline.

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const ALL_MONTHS = MONTHS.map((_, i) => i);

// First month (0-11) a baseline actually forecast. Months before it were already closed when it was
// locked, so the baseline counts them at actuals -- the way Finance's prior-month package does.
export function baselineStart(period) {
  const m = /^M(\d{2})$/.exec(period || '');
  if (m) return Number(m[1]) - 1;
  const q = /^Q([1-4])$/.exec(period || '');
  return q ? (Number(q[1]) - 1) * 3 : 0;
}

export function baselineLabel(b) {
  if (!b) return 'No baseline';
  const m = /^M(\d{2})$/.exec(b.period);
  if (m) return `${MONTHS[Number(m[1]) - 1]} ${b.year} monthly FCST`;
  return b.period === 'Annual' ? `${b.year} annual baseline` : `${b.year} ${b.period} baseline`;
}

// What the "Compare to" choice means, in plain words -- users couldn't tell the options apart
// (Judy, 2026-10-30). `b` null is the live Pulse FCST.
const lockedOn = (b) => new Date(b.lockedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
export function compareOption(b) {
  if (!b) return 'Pulse FCST — each month as planned when it began';
  return `${baselineLabel(b)} — locked ${lockedOn(b)}`;
}
export function compareHelp(b) {
  if (!b) {
    return 'Each month is compared to the forecast Resource Planning held for it on the day that month began (Pulse freezes a month\'s forecast once it starts). '
      + 'Answers: did each month deliver what was planned going into it?';
  }
  const from = MONTHS[baselineStart(b.period)];
  const lead = b.period === 'Annual'
    ? `The whole-year plan as it stood on ${lockedOn(b)}. Every month is compared to it.`
    : `A snapshot of the forecast taken ${lockedOn(b)}. Months before ${from} count at actuals (they were already closed); ${from} onward is compared to the snapshot.`;
  return `${lead} Priced at the billing rates known on that date, so a later rate change shows as rate variance. `
    + (b.period === 'Annual' ? 'Answers: how are we doing against the original plan?' : 'Answers: how has the outlook moved since that snapshot?');
}

// Rate in effect on the month's last day. `knownAt` limits it to rates entered by then, so a baseline
// is priced with the rates it could have known about.
export function rateAt(rates, year, m, knownAt) {
  const monthEnd = new Date(year, m + 1, 0, 23, 59, 59);
  let rate = null;
  let earliest = null;
  for (const r of rates || []) {
    if (knownAt && new Date(r.createdAt) > new Date(knownAt)) continue;
    if (earliest == null) earliest = r.rate;
    if (new Date(r.effectiveDate) > monthEnd) continue;
    rate = r.rate;
  }
  // Months before a project's first rate use that first rate: most rates were entered recently with
  // today as the effective date, and leaving earlier months at $0 hid real revenue (Judy, 2026-10-29).
  return rate ?? earliest;
}

// Monthly amount of a Ratable rate in effect that month. Unlike rateAt there's no back-fill before
// the first row: ratable revenue starts when Finance starts recognizing it.
// Compared as dates, not instants: a 1st-of-month date stored at UTC midnight is the prior day locally.
export function ratableAt(rows, year, m) {
  const monthEnd = `${year}-${String(m + 1).padStart(2, '0')}-31`;
  let amt = null;
  for (const r of rows || []) if (String(r.effectiveDate).slice(0, 10) <= monthEnd) amt = r.rate;
  return Number(amt) > 0 ? amt : null; // a 0 row ends the fee
}

// Last month (0-11) a Completed/Archived project still earns in `year`: its target end. Nothing after
// it is recorded (MNCCC closed out in Sep with Oct hours still forecast -- Judy, 2026-11-02).
export function closeOutMonth(project, year) {
  if (!['Completed', 'Archived'].includes(project?.projectStatus) || !project.targetEndDate) return 11;
  const end = new Date(project.targetEndDate);
  if (end.getFullYear() < year) return -1;
  return end.getFullYear() > year ? 11 : end.getMonth();
}

// Package line each record lands on in Finance's package; the page shows the lines picked, POC by
// default (Judy, 2026-11-02). Milestone lines earn on sign-off, Recurring IT a flat monthly amount.
export const LINES = { POC: 'POC', POC_MS: 'POC (Milestone)', HOSTING: 'Hosting', BB: 'B&B', TM: 'T&M', RECURRING: 'Recurring IT' };
export const LINE_ORDER = Object.values(LINES);
const UNPRICED_LINES = [LINES.POC_MS, LINES.BB, LINES.RECURRING];
const TYPE_LINE = { 'POC (Milestone)': LINES.POC_MS, Hosting: LINES.HOSTING, 'B&B': LINES.BB, 'T&M': LINES.TM };

// First month (0-11) that hasn't closed yet -- the month in progress.
export function openMonth(data) {
  const m = (data.months || []).find((x) => !x.closed);
  return m ? m.month - 1 : null;
}

// One record per (person/team/R&D row, month) with hours and dollars already priced.
// actualsToDate counts the month in progress at the hours logged so far instead of its forecast.
export function normalize(data, { actualsToDate = false } = {}) {
  const months = data.months || [];
  const monthOf = new Map(months.map((m) => [m.periodMonth, m]));
  const closed = new Set(months.filter((m) => m.closed).map((m) => m.month - 1));
  const current = openMonth(data);
  if (actualsToDate && current != null) closed.add(current);
  const projects = new Map((data.projects || []).map((p) => [p.id, p]));
  const ratesBy = new Map();
  const ratableBy = new Map();
  const tempBy = new Map();
  for (const r of data.rates || []) {
    const into = r.kind === 'Ratable' ? ratableBy : r.kind === 'Provisional' ? tempBy : ratesBy;
    if (!into.has(r.projectId)) into.set(r.projectId, []);
    into.get(r.projectId).push(r);
  }
  for (const list of [...ratesBy.values(), ...ratableBy.values(), ...tempBy.values()]) list.sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate));
  const bl = data.baseline;
  const start = bl ? baselineStart(bl.period) : 12;
  const lastMonth = new Map([...projects.values()].map((p) => [p.id, closeOutMonth(p, data.year)]));
  const ratable = (p, m) => ratableAt(ratableBy.get(p), data.year, m);
  const typeLine = (p) => TYPE_LINE[contractType(projects.get(p))] || LINES.POC;
  // A plain-POC project on a ratable rate is all Recurring IT; a milestone project keeps its hours on
  // its own line and earns both the ratable amount and its sign-offs (Cuyahoga -- Judy, 2026-11-02).
  const lineOf = (p, m) => (ratable(p, m) != null && typeLine(p) === LINES.POC ? LINES.RECURRING : typeLine(p));
  // A Provisional rate stands in only until Finance gives the official Services rate.
  const priceAt = (p, m, knownAt) => {
    const official = rateAt(ratesBy.get(p), data.year, m, knownAt);
    return official != null ? { rate: official, temp: false } : { rate: rateAt(tempBy.get(p), data.year, m, knownAt), temp: true };
  };
  const records = [];
  for (const row of data.rows || []) {
    // T&M isn't forecast, so its FCST is left out rather than compared against (Judy, 2026-10-30).
    const noFcst = contractType(projects.get(row.projectId)) === 'T&M';
    for (const [periodMonth, raw] of Object.entries(row.byPeriod || {})) {
      const cell = noFcst ? { ...raw, fcst: 0, bl: 0 } : raw;
      const mm = monthOf.get(periodMonth);
      if (!mm) continue;
      const m = mm.month - 1;
      if (m > (lastMonth.get(row.projectId) ?? 11)) continue;
      const isClosed = closed.has(m);
      // Ratable and milestone lines keep their hours but earn the flat amount / sign-offs below, not hours x rate.
      const line = lineOf(row.projectId, m);
      const priced = !UNPRICED_LINES.includes(line) && ratable(row.projectId, m) == null;
      const { rate, temp } = priced ? priceAt(row.projectId, m) : { rate: 0, temp: false };
      const blRate = bl && priced ? priceAt(row.projectId, m, bl.lockedAt).rate : null;
      const outH = isClosed ? cell.act : cell.fcst;
      // No baseline picked: compare to Pulse's own forecast, which Resource Planning freezes once a
      // month starts -- the same comparison as Time Reports' FCST vs Actuals (Judy, 2026-10-29).
      const baseH = !bl ? cell.fcst : m < start ? cell.act : cell.bl;
      const baseRate = !bl || m < start ? rate : blRate;
      records.push({
        p: row.projectId, key: row.key, name: row.name, kind: row.kind, team: row.teamName || '—',
        stream: row.kind === 'rnd' ? 'RD' : 'PS', line, m, closed: isClosed,
        actH: isClosed ? cell.act : 0, outH, baseH,
        act: isClosed ? cell.act * (rate || 0) : 0,
        out: outH * (rate || 0),
        base: baseH * (baseRate || 0),
        noRate: rate == null && (outH > 0 || baseH > 0),
        tempRate: temp && rate != null && (outH > 0 || baseH > 0) ? rate : null,
      });
    }
  }
  // Ratable revenue: Finance recognizes a flat amount each month regardless of hours, so ACT = FCST.
  for (const [p] of ratableBy) {
    for (const mm of months) {
      const m = mm.month - 1;
      const amt = ratable(p, m);
      if (amt == null || m > (lastMonth.get(p) ?? 11)) continue;
      const isClosed = closed.has(m);
      records.push({
        p, key: `ratable:${p}`, name: 'Ratable revenue', kind: 'ratable', team: 'Ratable',
        stream: 'PS', line: LINES.RECURRING, m, closed: isClosed, actH: 0, outH: 0, baseH: 0,
        act: isClosed ? amt : 0, out: amt, base: amt, noRate: false,
      });
    }
  }
  // Post-go-live pickup: one PS record per project in its pickup month, priced like any other hours.
  // Baseline = outlook so it never shows as variance (it's budget parked, not a forecast miss).
  const openMonths = new Set((data.months || []).map((x) => x.month - 1));
  for (const pk of data.pickups || []) {
    const m = pk.month - 1;
    if (!openMonths.has(m) || closed.has(m)) continue; // a closed month already has real actuals
    if (m > (lastMonth.get(pk.projectId) ?? 11) || UNPRICED_LINES.includes(lineOf(pk.projectId, m))) continue;
    const { rate } = priceAt(pk.projectId, m);
    const amount = pk.hours * (rate || 0);
    records.push({
      p: pk.projectId, key: `pickup:${pk.projectId}`, name: 'Post-go-live pickup', kind: 'pickup', team: 'Pickup',
      stream: 'PS', line: lineOf(pk.projectId, m), m, closed: false, actH: 0, outH: pk.hours, baseH: pk.hours,
      act: 0, out: amount, base: amount, noRate: rate == null,
    });
  }
  // B&B / POC (Milestone) sign-offs: the milestone amount is ACT in the month it's signed and FCST in its target month.
  // An unsigned milestone in a still-open month counts toward the outlook at its target.
  const monthIn = (d) => (d && Number(String(d).slice(0, 4)) === data.year ? Number(String(d).slice(5, 7)) - 1 : null);
  for (const ms of data.signoffs || []) {
    // Revenue is recognized at net -- gross less COGS (Judy, 2026-11-02); gross/COGS show on the POC (Milestone) card.
    const amt = ms.amount - (ms.cogs || 0);
    const signed = monthIn(ms.dateSigned);
    const target = monthIn(ms.targetDate);
    const rec = (m, f) => records.push({
      p: ms.projectId, key: `signoff:${ms.projectId}`, name: `Sign-off: ${ms.title}`, kind: 'signoff', team: 'Sign-off',
      stream: 'PS', line: typeLine(ms.projectId), m, closed: closed.has(m), actH: 0, outH: 0, baseH: 0, act: 0, out: 0, base: 0, noRate: false, ...f,
    });
    if (signed != null && openMonths.has(signed)) rec(signed, { act: closed.has(signed) ? amt : 0, out: amt });
    if (target != null && openMonths.has(target)) {
      rec(target, { base: amt, out: ms.dateSigned || closed.has(target) ? 0 : amt });
    }
  }
  // The milestones behind the sign-off records, with COGS and net for the Milestone sign-offs card.
  const signoffs = (data.signoffs || []).map((ms) => ({ ...ms, line: typeLine(ms.projectId), net: ms.amount - (ms.cogs || 0) }));
  return { year: data.year, months, closed, projects, ratesBy, baseline: bl, start, records, signoffs };
}

export const lastClosedMonth = (norm) => (norm.closed.size ? Math.max(...norm.closed) : null);

export function agg(records, months) {
  const ms = new Set(months);
  let out = 0, base = 0, act = 0, outH = 0, baseH = 0, actH = 0, ps = 0, rd = 0, psBase = 0, rdBase = 0, psH = 0, rdH = 0;
  let anyClosed = false, any = false;
  for (const r of records) {
    if (!ms.has(r.m)) continue;
    any = true;
    if (r.closed) anyClosed = true;
    out += r.out; base += r.base; act += r.act; outH += r.outH; baseH += r.baseH; actH += r.actH;
    if (r.stream === 'RD') { rd += r.out; rdBase += r.base; rdH += r.outH - r.baseH; } else { ps += r.out; psBase += r.base; psH += r.outH - r.baseH; }
  }
  // A period with no actuals yet (all future months) has nothing to compare: ACT is $0 and the
  // variance is blank rather than a misleading 100% of forecast (Judy, 2026-10-29).
  if (any && !anyClosed) {
    return {
      out: 0, base, act: 0, outH: 0, baseH, actH: 0, rate: 0, future: true,
      ps: 0, psBase, psVar: null, psPct: null, rd: 0, rdBase, rdVar: null, rdPct: null, psHVar: null, rdHVar: null,
      var: null, pct: null, volVar: null, rateVar: null,
    };
  }
  const rateO = outH ? out / outH : 0;
  const rateB = baseH ? base / baseH : 0;
  return {
    out, base, act, outH, baseH, actH, rate: rateO,
    // PS and R&D apart, so an over/under can be traced to one or the other.
    ps, psBase, psVar: ps - psBase, psPct: psBase ? ((ps - psBase) / psBase) * 100 : null,
    rd, rdBase, rdVar: rd - rdBase, rdPct: rdBase ? ((rd - rdBase) / rdBase) * 100 : null,
    psHVar: psH, rdHVar: rdH,
    var: out - base, pct: base ? ((out - base) / base) * 100 : null,
    // Hours effect at the baseline rate, plus rate effect on the hours actually worked/forecast.
    volVar: (outH - baseH) * rateB, rateVar: (rateO - rateB) * outH,
  };
}

// Variance month by month plus its running total -- how the gap builds up over the year.
export function runningVariance(records, months = ALL_MONTHS) {
  let total = 0;
  return months.map((m) => {
    const a = agg(records, [m]);
    total += a.var || 0;
    return { m, var: a.var, running: total, out: a.out, base: a.base };
  });
}

// Green at or over forecast, orange up to `band`% under, red further under -- being over is never a
// warning (Judy, 2026-10-30).
// With no FCST there's no %, so the sign of the $ variance decides: ACT over a $0 FCST is over, not
// "slightly under" (Bonneville Platform, Judy 2026-11-02).
export function status(pct, band, v) {
  if (pct == null) return v > 0 ? 'good' : v < 0 ? 'bad' : 'warn';
  if (pct >= 0) return 'good';
  return pct >= -band ? 'warn' : 'bad';
}

// Hosting work has no project type of its own -- it's a project named "...Hosting...", so it's split
// out by name (Judy, 2026-10-29).
export const CONTRACT_TYPES = ['POC', 'Hosting', 'T&M'];
export function contractType(project) {
  if (/hosting/i.test(project?.name || '')) return 'Hosting';
  return project?.projectType || '—';
}

export function dims(norm) {
  const proj = (r) => norm.projects.get(r.p) || {};
  return {
    project: { label: 'Project', key: (r) => r.p, name: (k) => norm.projects.get(k)?.name || 'Unknown project' },
    team: { label: 'Team', key: (r) => r.team, name: (k) => k },
    resource: { label: 'Resource', key: (r) => r.key, name: (k, r) => r?.name || k },
    state: { label: 'State', key: (r) => proj(r).state || '—', name: (k) => k },
    county: { label: 'County', key: (r) => proj(r).county || '—', name: (k) => k },
    type: { label: 'Package line', key: (r) => r.line, name: (k) => k },
    pm: { label: 'Project manager', key: (r) => proj(r).pm || '—', name: (k) => k },
  };
}

export const NEXT_DIM = { project: 'resource', team: 'resource', resource: 'project', state: 'county', county: 'project', type: 'project', pm: 'project' };

export function groupBy(records, keyFn) {
  const g = new Map();
  for (const r of records) {
    const k = keyFn(r);
    if (!g.has(k)) g.set(k, []);
    g.get(k).push(r);
  }
  return g;
}

export function periodMonths(grain, month, qtr) {
  if (grain === 'month') return [month];
  if (grain === 'qtr') return [qtr * 3, qtr * 3 + 1, qtr * 3 + 2];
  return ALL_MONTHS;
}

// Hover help for each package-line button.
export const LINE_NOTE = {
  [LINES.POC]: 'POC: hours x billing rate vs FCST',
  [LINES.POC_MS]: 'POC (Milestone): milestone amounts on customer sign-off (ACT) vs target date (FCST), not hours',
  [LINES.HOSTING]: 'Hosting projects: hours x billing rate',
  [LINES.BB]: 'Book & Bill: milestone amounts on sign-off (ACT) vs target date (FCST), not hours',
  [LINES.TM]: 'T&M: no FCST by design, so every dollar is upside',
  [LINES.RECURRING]: 'Recurring IT: flat monthly amount recognized ratably, ACT = FCST',
};

// Row order picked once for every table and heatmap: 'low' (most under first), 'high' (most over first) or 'alpha'.
export const ROW_ORDERS = [['low', 'Most under FCST first'], ['high', 'Most over FCST first'], ['alpha', 'A–Z']];
export function rowCompare(order, name, v) {
  return (a, b) => (order === 'alpha' ? String(name(a)).localeCompare(String(name(b)))
    : order === 'high' ? (v(b) ?? 0) - (v(a) ?? 0) : (v(a) ?? 0) - (v(b) ?? 0));
}
